import '@fontsource/ibm-plex-sans/400.css';
import '@fontsource/ibm-plex-sans/500.css';
import '@fontsource/ibm-plex-sans/600.css';
import '@fontsource/ibm-plex-sans/700.css';
import '@fontsource/ibm-plex-mono/400.css';
import '@fontsource/ibm-plex-mono/500.css';
import './styles/app.css';

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { I18nextProvider } from 'react-i18next';
import { AslenProvider } from '@aslen/core/hooks';
import { toAppError } from '@aslen/core/api';
import { routeTree } from './routeTree.gen';
import { i18n } from './lib/i18n';
import { kvStore, supabase } from './lib/supabase';
import { applyTheme, initialTheme } from './lib/theme';
import { ToastProvider } from './components/ui/toast';
import { QuickActionsProvider } from './components/shell/quick-actions';

applyTheme(initialTheme());

// DEV-ONLY: `?preview` renders every screen with sample rows (no Supabase needed).
const previewMode = (() => {
  if (!import.meta.env.DEV) return false;
  try {
    const q = new URLSearchParams(window.location.search).get('preview');
    if (q === '0') sessionStorage.removeItem('aslen.preview');
    else if (q !== null) sessionStorage.setItem('aslen.preview', '1');
    return sessionStorage.getItem('aslen.preview') === '1';
  } catch {
    return false;
  }
})();
// `import.meta.env.DEV &&` is folded to `false` in production builds, so the mock chunk is not even emitted
const devPreview = import.meta.env.DEV && previewMode ? await import('./dev/mock-client') : null;
const client = devPreview ? devPreview.createMockClient() : supabase;
const previewSession = devPreview ? devPreview.previewSession() : undefined;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: previewMode ? Infinity : 30_000,
      retry: (count, err) => count < 2 && toAppError(err).code === 'network',
      refetchOnWindowFocus: true,
    },
  },
});

/** DEV-ONLY: impossible to miss that the screen shows sample rows, with a one-click exit. */
function PreviewBadge() {
  return (
    <a
      href="?preview=0"
      onClick={() => sessionStorage.removeItem('aslen.preview')}
      className="fixed bottom-3 left-1/2 z-[60] -translate-x-1/2 rounded-pill bg-warning px-3.5 py-1.5 text-[12px] font-semibold text-white shadow-popover"
    >
      ÖNİZLEME · örnek veri · Çık ✕
    </a>
  );
}

const router = createRouter({ routeTree, defaultPreload: 'intent', scrollRestoration: true });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// A dev reload re-runs this module, and createRoot on an already-rooted container
// mounts the app a second time — keep the root and re-render it instead.
const container = document.getElementById('root')! as HTMLElement & { __aslenRoot?: ReturnType<typeof createRoot> };
container.__aslenRoot ??= createRoot(container);
container.__aslenRoot.render(
  <StrictMode>
    <I18nextProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        <AslenProvider client={client} store={kvStore} previewSession={previewSession}>
          <ToastProvider>
            <QuickActionsProvider>
              <RouterProvider router={router} />
              {previewMode && <PreviewBadge />}
            </QuickActionsProvider>
          </ToastProvider>
        </AslenProvider>
      </QueryClientProvider>
    </I18nextProvider>
  </StrictMode>,
);
